import { ASSET_MANIFEST } from '../assets/manifest';
import { t } from '../i18n';
import { CELL_SIZE, COLS, DIR_VECTORS, ROWS, VIEW_HEIGHT, VIEW_WIDTH } from './constants';
import { TileType, type BattleDecalEntity, type BulletEntity, type Direction, type EnvironmentKind, type ExplosionEffectEntity, type ParticleEntity, type PowerupEntity, type TankEntity } from './types';
import type { GameEngine } from './GameEngine';
import { clamp } from './utils';

type TickerCallback = (ticker: { deltaMS: number }) => void;

const TILE_COLORS: Record<TileType, number> = {
  [TileType.Empty]: 0x000000,
  [TileType.Brick]: 0xb97842,
  [TileType.Metal]: 0x8f938c,
  [TileType.Water]: 0x2d78a7,
  [TileType.BaseCore]: 0xd8a742,
  [TileType.BaseWall]: 0xc5a06b,
  [TileType.ReinforcedWall]: 0x8d9691,
};

const POWERUP_COLORS: Record<string, number> = {
  rapid: 0xf0c84b,
  shield: 0x5bd3ff,
  bomb: 0xff6754,
  life: 0x6ee46c,
  freeze: 0x91d8ff,
  repair: 0xd6b378,
  baseShield: 0x78f4ff,
  jam: 0xb995ff,
  upgrade: 0xd6c64f,
  heal: 0xfff2f2,
};

const POWERUP_ORDER = ['rapid', 'shield', 'bomb', 'life', 'freeze', 'repair', 'baseShield', 'jam'];
const EXTRA_POWERUP_ORDER = ['upgrade', 'heal'];
const PLAYER_RECOLOR_TARGETS = {
  p3: { hue: 278, saturation: 0.5 },
  p4: { hue: 43, saturation: 0.56 },
} as const;

const TILE_CRACK_PATTERNS = [
  {
    main: [7, 10, 15, 16, 12, 24, 21, 32, 18, 36],
    branches: [[14, 17, 24, 14, 30, 9], [13, 24, 7, 29, 5, 35]],
    severe: [[21, 32, 30, 30, 36, 24], [17, 35, 25, 38]],
    chips: [[26, 17, 1.6], [10, 31, 1.2], [31, 28, 1.4]],
  },
  {
    main: [29, 7, 23, 15, 26, 22, 17, 30, 19, 37],
    branches: [[24, 16, 15, 13, 8, 8], [25, 22, 33, 27, 36, 34]],
    severe: [[17, 30, 9, 28, 4, 22], [20, 37, 28, 36]],
    chips: [[13, 15, 1.5], [32, 31, 1.3], [20, 27, 1.7]],
  },
  {
    main: [18, 6, 15, 15, 22, 21, 17, 29, 24, 37],
    branches: [[16, 15, 8, 18, 4, 24], [21, 22, 29, 18, 35, 13]],
    severe: [[18, 29, 10, 35, 6, 38], [23, 36, 33, 33, 37, 29]],
    chips: [[28, 15, 1.3], [11, 23, 1.5], [27, 31, 1.4]],
  },
] as const;

class FrameTicker {
  private callbacks: TickerCallback[] = [];
  private running = false;
  private last = 0;

  add(callback: TickerCallback): void {
    this.callbacks.push(callback);
    if (!this.running) this.start();
  }

  private start(): void {
    this.running = true;
    this.last = performance.now();
    const frame = (now: number) => {
      const deltaMS = Math.min(100, now - this.last);
      this.last = now;
      for (const callback of this.callbacks) callback({ deltaMS });
      requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  }
}

export class CanvasGameView {
  app = { ticker: new FrameTicker() };
  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  private resolution = 1;
  private fallbackFloor?: HTMLImageElement;
  private floorTextures = new Map<EnvironmentKind, HTMLCanvasElement>();
  private tileTextures = new Map<TileType, HTMLCanvasElement>();
  private baseTextures = new Map<'normal' | 'shielded', HTMLCanvasElement>();
  private tankTextures = new Map<string, HTMLCanvasElement>();
  private powerupTextures = new Map<string, HTMLCanvasElement>();
  private terrainDetailTextures = new Map<number, HTMLCanvasElement>();
  private shake = 0;
  private showAiDebug = false;

  async init(root: HTMLElement): Promise<void> {
    this.resolution = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas = document.createElement('canvas');
    this.canvas.width = Math.round(VIEW_WIDTH * this.resolution);
    this.canvas.height = Math.round(VIEW_HEIGHT * this.resolution);
    this.canvas.setAttribute('aria-label', t('app.canvasLabel'));
    this.ctx = requiredContext(this.canvas);
    this.ctx.imageSmoothingEnabled = true;
    root.appendChild(this.canvas);
    await this.loadTextures();
  }

  addShake(amount: number): void {
    this.shake = Math.min(20, Math.max(this.shake, amount));
  }

  refreshLocale(): void {
    this.canvas.setAttribute('aria-label', t('app.canvasLabel'));
  }

  setDebugOptions(options: { showAiDebug?: boolean }): void {
    this.showAiDebug = options.showAiDebug ?? this.showAiDebug;
  }

  render(engine: GameEngine): void {
    const ctx = this.ctx;
    const offsetX = this.shake > 0 ? (Math.random() - 0.5) * this.shake * 2 : 0;
    const offsetY = this.shake > 0 ? (Math.random() - 0.5) * this.shake * 2 : 0;
    this.shake *= 0.88;
    if (this.shake < 0.1) this.shake = 0;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.setTransform(this.resolution, 0, 0, this.resolution, 0, 0);
    ctx.imageSmoothingEnabled = true;
    ctx.save();
    ctx.translate(offsetX, offsetY);
    this.drawFloor(engine.getEnvironment());
    this.drawMap(engine);
    this.drawExplosionEffects(engine.effects);
    for (const powerup of engine.powerups) this.drawPowerup(powerup);
    for (const enemy of engine.enemies) this.drawTank(enemy);
    for (const player of engine.players) this.drawTank(player);
    for (const bullet of engine.bullets) this.drawBullet(bullet);
    for (const particle of engine.particles) this.drawParticle(particle);
    this.drawBaseShield(engine);
    if (this.showAiDebug) this.drawEnemyDebugInfo(engine.enemies);
    this.drawStatusLabel(engine);
    ctx.restore();
  }

  private async loadTextures(): Promise<void> {
    this.fallbackFloor = await loadImage(ASSET_MANIFEST.generatedFloor).catch(() => undefined);
    try {
      const [environment, terrain, tanks, bossSiegeTanks, bossHunterTanks, bossBulwarkTanks, bossSniperTanks, heavyTanks, sniperTanks, playerUpgrades, player2Upgrades, playerAssaultClasses, playerHeavyClasses, player2AssaultClasses, player2HeavyClasses, powerups, extraPowerups, detailDecals] = await Promise.all([
        loadImage(ASSET_MANIFEST.environmentAtlas),
        loadImage(ASSET_MANIFEST.terrainAtlas),
        loadImage(ASSET_MANIFEST.tankAtlas),
        loadImage(ASSET_MANIFEST.bossSiegeAtlas),
        loadImage(ASSET_MANIFEST.bossHunterAtlas),
        loadImage(ASSET_MANIFEST.bossBulwarkAtlas),
        loadImage(ASSET_MANIFEST.bossSniperAtlas),
        loadImage(ASSET_MANIFEST.heavyTankAtlas),
        loadImage(ASSET_MANIFEST.sniperTankAtlas),
        loadImage(ASSET_MANIFEST.playerUpgradeAtlas),
        loadImage(ASSET_MANIFEST.player2UpgradeAtlas),
        loadImage(ASSET_MANIFEST.playerAssaultClassAtlas),
        loadImage(ASSET_MANIFEST.playerHeavyClassAtlas),
        loadImage(ASSET_MANIFEST.player2AssaultClassAtlas),
        loadImage(ASSET_MANIFEST.player2HeavyClassAtlas),
        loadImage(ASSET_MANIFEST.powerupAtlas),
        loadImage(ASSET_MANIFEST.powerupExtraAtlas),
        loadImage(ASSET_MANIFEST.terrainDetailDecals),
      ]);
      this.floorTextures.set('desert', cropToCanvas(environment, 3, 1, 0, 0, 768, 768, false));
      this.floorTextures.set('forest', cropToCanvas(environment, 3, 1, 1, 0, 768, 768, false));
      this.floorTextures.set('snow', cropToCanvas(environment, 3, 1, 2, 0, 768, 768, false));
      this.tileTextures.set(TileType.Brick, cropToCanvas(terrain, 4, 2, 0, 0, 72, 72, true, 34));
      this.tileTextures.set(TileType.Metal, cropToCanvas(terrain, 4, 2, 1, 0, 72, 72, true, 34));
      this.tileTextures.set(TileType.Water, cropToCanvas(terrain, 4, 2, 2, 0, 72, 72, true, 34));
      this.tileTextures.set(TileType.BaseWall, cropToCanvas(terrain, 4, 2, 3, 0, 72, 72, true, 34));
      this.tileTextures.set(TileType.ReinforcedWall, cropToCanvas(terrain, 4, 2, 3, 0, 72, 72, true, 34));
      this.baseTextures.set('normal', cropToCanvas(terrain, 4, 2, 0, 1, 144, 144, true, 34));
      this.baseTextures.set('shielded', cropToCanvas(terrain, 4, 2, 1, 1, 144, 144, true, 34));

      const tankTypes = ['p1', 'p2', 'light', 'assault', 'boss'];
      const directions = ['up', 'right', 'down', 'left'];
      for (let row = 0; row < directions.length; row += 1) {
        for (let col = 0; col < tankTypes.length; col += 1) {
          const texture = cropToCanvas(tanks, 5, 4, col, row, 96, 96, true);
          this.tankTextures.set(`${tankTypes[col]}:${directions[row]}`, texture);
          if (tankTypes[col] === 'p1') {
            this.tankTextures.set(`p3:${directions[row]}`, recolorPlayerCanvas(texture, PLAYER_RECOLOR_TARGETS.p3.hue, PLAYER_RECOLOR_TARGETS.p3.saturation));
          } else if (tankTypes[col] === 'p2') {
            this.tankTextures.set(`p4:${directions[row]}`, recolorPlayerCanvas(texture, PLAYER_RECOLOR_TARGETS.p4.hue, PLAYER_RECOLOR_TARGETS.p4.saturation));
          }
        }
        this.tankTextures.set(`boss-siege:${directions[row]}`, cropToCanvas(bossSiegeTanks, 1, 4, 0, row, 96, 96, false));
        this.tankTextures.set(`boss-hunter:${directions[row]}`, cropToCanvas(bossHunterTanks, 1, 4, 0, row, 96, 96, false));
        this.tankTextures.set(`boss-bulwark:${directions[row]}`, cropToCanvas(bossBulwarkTanks, 1, 4, 0, row, 96, 96, false));
        this.tankTextures.set(`boss-sniper:${directions[row]}`, cropToCanvas(bossSniperTanks, 1, 4, 0, row, 96, 96, false));
        this.tankTextures.set(`heavy:${directions[row]}`, cropToCanvas(heavyTanks, 1, 4, 0, row, 96, 96, false));
        this.tankTextures.set(`sniper:${directions[row]}`, cropToCanvas(sniperTanks, 1, 4, 0, row, 96, 96, false));
        for (let level = 1; level <= 4; level += 1) {
          const p1Upgrade = cropToCanvas(playerUpgrades, 4, 4, level - 1, row, 96, 96, false);
          const p2Upgrade = cropToCanvas(player2Upgrades, 4, 4, level - 1, row, 96, 96, false);
          const p1Assault = cropToCanvas(playerAssaultClasses, 4, 4, level - 1, row, 96, 96, false);
          const p1Heavy = cropToCanvas(playerHeavyClasses, 4, 4, level - 1, row, 96, 96, false);
          const p2Assault = cropToCanvas(player2AssaultClasses, 4, 4, level - 1, row, 96, 96, false);
          const p2Heavy = cropToCanvas(player2HeavyClasses, 4, 4, level - 1, row, 96, 96, false);
          this.tankTextures.set(`upgrade${level}:${directions[row]}`, p1Upgrade);
          this.tankTextures.set(`p2upgrade${level}:${directions[row]}`, p2Upgrade);
          this.tankTextures.set(`p3upgrade${level}:${directions[row]}`, recolorPlayerCanvas(p1Upgrade, PLAYER_RECOLOR_TARGETS.p3.hue, PLAYER_RECOLOR_TARGETS.p3.saturation));
          this.tankTextures.set(`p4upgrade${level}:${directions[row]}`, recolorPlayerCanvas(p2Upgrade, PLAYER_RECOLOR_TARGETS.p4.hue, PLAYER_RECOLOR_TARGETS.p4.saturation));
          this.tankTextures.set(`p1-assault-upgrade${level}:${directions[row]}`, p1Assault);
          this.tankTextures.set(`p1-heavy-upgrade${level}:${directions[row]}`, p1Heavy);
          this.tankTextures.set(`p2-assault-upgrade${level}:${directions[row]}`, p2Assault);
          this.tankTextures.set(`p2-heavy-upgrade${level}:${directions[row]}`, p2Heavy);
          this.tankTextures.set(`p3-assault-upgrade${level}:${directions[row]}`, recolorPlayerCanvas(p1Assault, PLAYER_RECOLOR_TARGETS.p3.hue, PLAYER_RECOLOR_TARGETS.p3.saturation));
          this.tankTextures.set(`p3-heavy-upgrade${level}:${directions[row]}`, recolorPlayerCanvas(p1Heavy, PLAYER_RECOLOR_TARGETS.p3.hue, PLAYER_RECOLOR_TARGETS.p3.saturation));
          this.tankTextures.set(`p4-assault-upgrade${level}:${directions[row]}`, recolorPlayerCanvas(p2Assault, PLAYER_RECOLOR_TARGETS.p4.hue, PLAYER_RECOLOR_TARGETS.p4.saturation));
          this.tankTextures.set(`p4-heavy-upgrade${level}:${directions[row]}`, recolorPlayerCanvas(p2Heavy, PLAYER_RECOLOR_TARGETS.p4.hue, PLAYER_RECOLOR_TARGETS.p4.saturation));
        }
      }
      for (let index = 0; index < POWERUP_ORDER.length; index += 1) {
        this.powerupTextures.set(POWERUP_ORDER[index], centerVisibleContent(cropToCanvas(powerups, 4, 2, index % 4, Math.floor(index / 4), 80, 80, false, 28), 66));
      }
      for (let index = 0; index < EXTRA_POWERUP_ORDER.length; index += 1) {
        this.powerupTextures.set(EXTRA_POWERUP_ORDER[index], centerVisibleContent(cropToCanvas(extraPowerups, 2, 1, index, 0, 80, 80, false, 10), 66));
      }
      for (let index = 0; index < 12; index += 1) {
        this.terrainDetailTextures.set(index, cropToCanvas(detailDecals, 4, 3, index % 4, Math.floor(index / 4), 96, 96, false, 22));
      }
    } catch {
      this.floorTextures.clear();
      this.tileTextures.clear();
      this.baseTextures.clear();
      this.tankTextures.clear();
      this.powerupTextures.clear();
      this.terrainDetailTextures.clear();
    }
  }

  private drawFloor(environment: EnvironmentKind): void {
    const base = environment === 'forest' ? 0x89a85e : environment === 'snow' ? 0xd8e5ea : 0xd2a05a;
    this.fillRect(0, 0, VIEW_WIDTH, VIEW_HEIGHT, base);
    const texture = this.floorTextures.get(environment);
    if (texture) {
      this.ctx.save();
      this.ctx.globalAlpha = 0.48;
      this.ctx.filter = 'saturate(0.68) contrast(0.68) brightness(1.14)';
      this.ctx.drawImage(texture, 0, 0, VIEW_WIDTH, VIEW_HEIGHT);
      this.ctx.restore();
    } else if (this.fallbackFloor) {
      this.ctx.save();
      this.ctx.globalAlpha = 0.42;
      this.ctx.drawImage(this.fallbackFloor, 0, 0, VIEW_WIDTH, VIEW_HEIGHT);
      this.ctx.restore();
    } else {
      this.fillRect(0, 0, VIEW_WIDTH, VIEW_HEIGHT, 0x11140f);
    }
    this.drawCartoonFloorOverlay(environment);
  }

  private drawCartoonFloorOverlay(environment: EnvironmentKind): void {
    const tint = environment === 'forest' ? 0x7ca86a : environment === 'snow' ? 0xdfe8ee : 0xc9944f;
    const ink = environment === 'snow' ? 0x8fa1ad : 0x684b2f;
    this.fillRect(0, 0, VIEW_WIDTH, VIEW_HEIGHT, tint, environment === 'snow' ? 0.34 : 0.28);
    this.ctx.save();
    this.ctx.globalAlpha = environment === 'snow' ? 0.16 : 0.12;
    this.ctx.fillStyle = toCss(environment === 'forest' ? 0x557b4f : environment === 'snow' ? 0xffffff : 0xb77a3d);
    for (let i = 0; i < 18; i += 1) {
      const x = ((i * 157) % VIEW_WIDTH) - 30;
      const y = ((i * 91) % VIEW_HEIGHT) - 20;
      this.ctx.beginPath();
      this.ctx.ellipse(x, y, 42 + (i % 4) * 18, 18 + (i % 3) * 10, (i * 0.7) % Math.PI, 0, Math.PI * 2);
      this.ctx.fill();
    }
    this.ctx.globalAlpha = environment === 'snow' ? 0.22 : 0.18;
    this.ctx.strokeStyle = toCss(ink);
    this.ctx.lineWidth = 3;
    this.ctx.setLineDash([10, 18]);
    for (let x = -60; x < VIEW_WIDTH + 80; x += 115) {
      this.ctx.beginPath();
      this.ctx.moveTo(x, -20);
      this.ctx.bezierCurveTo(x + 42, 140, x - 34, 280, x + 34, VIEW_HEIGHT + 20);
      this.ctx.stroke();
    }
    this.ctx.setLineDash([]);
    this.ctx.globalAlpha = 0.1;
    this.ctx.lineWidth = 1;
    for (let x = CELL_SIZE; x < VIEW_WIDTH; x += CELL_SIZE) {
      this.ctx.beginPath();
      this.ctx.moveTo(x, 0);
      this.ctx.lineTo(x, VIEW_HEIGHT);
      this.ctx.stroke();
    }
    for (let y = CELL_SIZE; y < VIEW_HEIGHT; y += CELL_SIZE) {
      this.ctx.beginPath();
      this.ctx.moveTo(0, y);
      this.ctx.lineTo(VIEW_WIDTH, y);
      this.ctx.stroke();
    }
    this.ctx.restore();
    this.strokeRoundRect(3, 3, VIEW_WIDTH - 6, VIEW_HEIGHT - 6, 10, 0x3f2b1a, 3, 0.28);
  }

  private drawMap(engine: GameEngine): void {
    this.drawTerrainDetails(engine);
    for (let row = 0; row < ROWS; row += 1) {
      for (let col = 0; col < COLS; col += 1) {
        const tile = engine.map[row][col];
        if (tile === TileType.Empty || tile === TileType.BaseCore) continue;
        const x = col * CELL_SIZE;
        const y = row * CELL_SIZE;
        if (!this.drawTileSprite(tile, x, y)) {
          if (tile === TileType.Brick) this.drawBrick(x, y);
          else if (tile === TileType.Metal) this.drawMetal(x, y);
          else if (tile === TileType.Water) this.drawWater(x, y);
          else if (tile === TileType.BaseWall) this.drawBaseWall(x, y);
          else if (tile === TileType.ReinforcedWall) this.drawReinforcedWall(x, y);
        }
        this.drawTileDamage(engine, tile, row, col, x, y);
      }
    }
    this.drawBattleDecals(engine.decals);
    this.drawBaseCoreCluster(engine);
  }

  private drawTileSprite(tile: TileType, x: number, y: number): boolean {
    const texture = this.tileTextures.get(tile);
    if (!texture) return false;
    this.ctx.drawImage(texture, x, y, CELL_SIZE, CELL_SIZE);
    return true;
  }

  private drawTerrainDetails(engine: GameEngine): void {
    if (this.terrainDetailTextures.size === 0) return;
    const environment = engine.getEnvironment();
    const choices = terrainDetailChoices(environment);
    const random = seededDetailRandom(engine.level * 92821 + environment.length * 137);
    let placed = 0;
    let attempts = 0;
    const target = 42;
    while (placed < target && attempts < target * 8) {
      attempts += 1;
      const col = Math.floor(random() * COLS);
      const row = Math.floor(random() * ROWS);
      if (engine.map[row][col] !== TileType.Empty) continue;
      if (row >= ROWS - 3 && col >= 7 && col <= 12) continue;
      const texture = this.terrainDetailTextures.get(choices[Math.floor(random() * choices.length)]);
      if (!texture) continue;
      const size = 32 + random() * 34;
      this.drawImage(texture, col * CELL_SIZE + 10 + random() * 20, row * CELL_SIZE + 10 + random() * 20, size, size, 0.22 + random() * 0.3, random() * Math.PI * 2);
      placed += 1;
    }
  }

  private drawBattleDecals(decals: BattleDecalEntity[]): void {
    for (const decal of decals) {
      const alpha = Math.max(0, Math.min(1, decal.life / decal.maxLife));
      const texture = this.terrainDetailTextures.get(decal.kind === 'crater' ? 11 : 3);
      if (texture) {
        this.drawImage(texture, decal.x, decal.y, decal.size, decal.size, decal.kind === 'crater' ? 0.42 * alpha : 0.55 * alpha, decal.rotation);
      } else {
        this.fillCircle(decal.x, decal.y, decal.size * 0.3, 0x1e1712, 0.38 * alpha);
      }
    }
  }

  private drawBrick(x: number, y: number): void {
    this.fillRect(x + 1, y + 1, 38, 38, 0x9c6239);
    this.fillRect(x + 3, y + 3, 34, 14, 0xc9834f);
    this.fillRect(x + 3, y + 22, 34, 13, 0xb87545);
    this.fillRect(x + 1, y + 19, 38, 2, 0x74472d);
    this.strokeRect(x + 1, y + 1, 38, 38, 0x5f3b28);
  }

  private drawMetal(x: number, y: number): void {
    this.fillRect(x + 1, y + 1, 38, 38, 0x777b78);
    this.fillRect(x + 4, y + 4, 32, 12, 0xa8aaa1);
    this.fillRect(x + 4, y + 24, 32, 10, 0x5f6663);
    for (const [cx, cy] of [[8, 8], [32, 8], [8, 32], [32, 32]]) this.fillCircle(x + cx, y + cy, 3, 0x3c403f);
    this.strokeRect(x + 1, y + 1, 38, 38, 0xc3c5ba, 1, 0.45);
  }

  private drawWater(x: number, y: number): void {
    this.fillRect(x + 1, y + 1, 38, 38, 0x1f5f7f);
    this.fillRect(x + 5, y + 9, 28, 3, 0x7ac8e8, 0.36);
    this.fillRect(x + 8, y + 24, 24, 3, 0xa5e7ff, 0.26);
    this.strokeRect(x + 1, y + 1, 38, 38, 0x65bde1, 1, 0.35);
  }

  private drawBaseCore(x: number, y: number): void {
    this.fillRect(x + 2, y + 2, 36, 36, TILE_COLORS[TileType.BaseCore]);
    this.fillRect(x + 8, y + 8, 24, 24, 0x5c4530);
    this.drawPoly([x + 20, y + 8, x + 30, y + 20, x + 20, y + 32, x + 10, y + 20], 0xf2c25a);
    this.strokeRect(x + 2, y + 2, 36, 36, 0xffdf84, 2, 0.55);
  }

  private drawBaseCoreCluster(engine: GameEngine): void {
    let minRow = ROWS;
    let minCol = COLS;
    let found = false;
    for (let row = 0; row < ROWS; row += 1) {
      for (let col = 0; col < COLS; col += 1) {
        if (engine.map[row][col] !== TileType.BaseCore) continue;
        found = true;
        minRow = Math.min(minRow, row);
        minCol = Math.min(minCol, col);
      }
    }
    if (!found) return;
    const texture = this.baseTextures.get(engine.isBaseProtected() ? 'shielded' : 'normal');
    const x = minCol * CELL_SIZE;
    const y = minRow * CELL_SIZE;
    if (!texture) {
      this.drawBaseCore(x, y);
      this.drawBaseCore(x + CELL_SIZE, y);
      this.drawBaseCore(x, y + CELL_SIZE);
      this.drawBaseCore(x + CELL_SIZE, y + CELL_SIZE);
      return;
    }
    const spriteSize = CELL_SIZE * 2 + 16;
    this.ctx.drawImage(texture, x - 8, Math.min(y - 16, VIEW_HEIGHT - spriteSize), spriteSize, spriteSize);
    const hp = engine.getTileHp(minRow, minCol);
    if (hp < 3) this.drawBaseCoreDamageCracks(x, y, hp);
  }

  private drawBaseWall(x: number, y: number): void {
    this.fillRect(x + 1, y + 1, 38, 38, TILE_COLORS[TileType.BaseWall]);
    this.fillRect(x + 3, y + 3, 34, 15, 0xd3b17d);
    this.fillRect(x + 3, y + 23, 34, 12, 0xae8759);
    this.strokeRect(x + 1, y + 1, 38, 38, 0x7f6040);
  }

  private drawReinforcedWall(x: number, y: number): void {
    this.fillRect(x + 1, y + 1, 38, 38, TILE_COLORS[TileType.ReinforcedWall]);
    this.fillRect(x + 4, y + 4, 32, 32, 0xb8c0ba);
    this.strokeRect(x + 7, y + 7, 26, 26, 0x56605c, 2, 0.7);
    this.strokeRect(x + 1, y + 1, 38, 38, 0x2f3734, 2, 0.5);
  }

  private drawTileDamage(engine: GameEngine, tile: TileType, row: number, col: number, x: number, y: number): void {
    const maxHp = engine.getTileMaxHp(tile);
    if (maxHp <= 1) return;
    const hp = engine.getTileHp(row, col);
    if (hp >= maxHp) return;
    const damage = 1 - hp / maxHp;
    this.drawStoneDamageCracks(x, y, damage, (row * 17 + col * 31) % TILE_CRACK_PATTERNS.length, tile === TileType.BaseWall);
  }

  private drawStoneDamageCracks(x: number, y: number, damage: number, variant: number, warmStone: boolean): void {
    const pattern = TILE_CRACK_PATTERNS[variant];
    const alpha = 0.34 + damage * 0.4;
    const dark = warmStone ? 0x251811 : 0x141817;
    const highlight = warmStone ? 0xf0cc95 : 0xd9e0d8;
    this.strokeRelativePath(x, y, pattern.main, highlight, 1, alpha * 0.22, 1, 1);
    this.strokeRelativePath(x, y, pattern.main, dark, 2.2, alpha);
    for (const branch of pattern.branches) {
      this.strokeRelativePath(x, y, branch, highlight, 0.8, alpha * 0.16, 1, 1);
      this.strokeRelativePath(x, y, branch, dark, 1.35, alpha * 0.78);
    }
    if (damage > 0.48) {
      for (const crack of pattern.severe) {
        this.strokeRelativePath(x, y, crack, highlight, 0.8, alpha * 0.14, 1, 1);
        this.strokeRelativePath(x, y, crack, dark, 1.45, alpha * 0.84);
      }
      for (const [cx, cy, radius] of pattern.chips) {
        this.fillCircle(x + cx, y + cy, radius, dark, alpha * 0.46);
        this.fillCircle(x + cx + 1.4, y + cy - 1.2, radius * 0.45, highlight, alpha * 0.18);
      }
    }
  }

  private drawBaseCoreDamageCracks(x: number, y: number, hp: number): void {
    const alpha = hp <= 1 ? 0.68 : 0.42;
    const dark = 0x251915;
    const highlight = 0xf2d09a;
    const paths = [
      [14, 16, 24, 24, 22, 36, 32, 48, 28, 63],
      [56, 14, 50, 28, 60, 39, 55, 55, 64, 66],
      [39, 10, 42, 22, 36, 33, 42, 44],
    ];
    for (const path of paths) {
      this.strokeRelativePath(x, y, path, highlight, 1.2, alpha * 0.18, 1, 1);
      this.strokeRelativePath(x, y, path, dark, 2.8, alpha);
    }
    const branches = hp <= 1
      ? [[24, 25, 14, 31, 8, 42], [31, 49, 43, 55, 50, 68], [51, 29, 42, 31, 36, 39], [59, 41, 70, 45, 76, 54]]
      : [[24, 25, 16, 31], [55, 55, 67, 59]];
    for (const path of branches) this.strokeRelativePath(x, y, path, dark, 1.75, alpha * 0.85);
    if (hp <= 1) {
      for (const [cx, cy, radius] of [[18, 39, 2.1], [45, 55, 2.4], [65, 34, 1.8], [36, 28, 1.5]]) {
        this.fillCircle(x + cx, y + cy, radius, dark, alpha * 0.42);
      }
    }
  }

  private strokeRelativePath(x: number, y: number, points: readonly number[], color: number, lineWidth = 1, alpha = 1, offsetX = 0, offsetY = 0): void {
    if (points.length < 4) return;
    const absolute = points.map((point, index) => point + (index % 2 === 0 ? x + offsetX : y + offsetY));
    this.strokePath(absolute, color, lineWidth, alpha);
  }

  private drawTank(tank: TankEntity): void {
    if (!tank.alive) return;
    if (tank.invincibleTimer > 0 && Math.floor(performance.now() / 85) % 2 === 0) return;
    if (!this.drawTankSprite(tank)) this.drawPrimitiveTank(tank);
    if (tank.team === 'enemy' && tank.shootWindupTimer > 0) this.drawMuzzleFlash(tank);
    this.drawTankHealthBar(tank);
  }

  private drawTankSprite(tank: TankEntity): boolean {
    const metrics = getTankRenderMetrics(tank);
    const texture = this.tankTextures.get(`${tankTextureKind(tank)}:${metrics.frame.direction}`);
    if (!texture) return false;
    this.drawImage(texture, metrics.centerX, metrics.centerY, metrics.size, metrics.size, 1, 0, metrics.frame.mirrorX);
    if (tank.shieldTimer > 0) this.drawShieldEffect(metrics.centerX, metrics.centerY, metrics.shieldRadius, tank.shieldTimer);
    return true;
  }

  private drawPrimitiveTank(tank: TankEntity): void {
    const s = tank.size;
    const h = s / 2;
    const horizontal = tank.direction === 'left' || tank.direction === 'right';
    const barrel = tank.direction === 'up'
      ? { x: tank.x - 3, y: tank.y - h - 8, w: 6, h: 20 }
      : tank.direction === 'down'
        ? { x: tank.x - 3, y: tank.y + h - 12, w: 6, h: 20 }
        : tank.direction === 'left'
          ? { x: tank.x - h - 8, y: tank.y - 3, w: 20, h: 6 }
          : { x: tank.x + h - 12, y: tank.y - 3, w: 20, h: 6 };
    this.fillRect(tank.x - h + 2, tank.y - h + 3, s, s, 0x000000, 0.28);
    if (horizontal) {
      this.fillRect(tank.x - h + 3, tank.y - h, s - 6, 6, 0x161914);
      this.fillRect(tank.x - h + 3, tank.y + h - 6, s - 6, 6, 0x161914);
    } else {
      this.fillRect(tank.x - h, tank.y - h + 3, 6, s - 6, 0x161914);
      this.fillRect(tank.x + h - 6, tank.y - h + 3, 6, s - 6, 0x161914);
    }
    this.roundRect(tank.x - h + 5, tank.y - h + 5, s - 10, s - 10, 5, tank.color);
    this.roundRect(tank.x - h + 8, tank.y - h + 7, s - 16, Math.max(8, s * 0.28), 3, tank.accent, 0.28);
    this.fillCircle(tank.x, tank.y, s * 0.23, tank.accent);
    this.fillRect(barrel.x, barrel.y, barrel.w, barrel.h, tank.accent);
    this.strokeRoundRect(tank.x - h + 5, tank.y - h + 5, s - 10, s - 10, 5, 0x10120f, 2, 0.62);
    if (tank.shieldTimer > 0) this.drawShieldEffect(tank.x, tank.y, tank.size * 0.86, tank.shieldTimer);
  }

  private drawTankHealthBar(tank: TankEntity): void {
    if (tank.hp <= 1) return;
    const ratio = Math.max(0, Math.min(1, tank.hp / tank.maxHp));
    const isBoss = tank.kind === 'boss';
    const width = isBoss ? tank.size + 12 : tank.size;
    const height = isBoss ? 6 : 4;
    const y = tank.y - tank.size / 2 - (isBoss ? 11 : 8);
    const fill = isBoss ? 0xff3d34 : tank.team === 'player' ? 0xd6e96c : 0xffc45a;
    this.fillRect(tank.x - width / 2, y, width, height, 0x301611);
    this.fillRect(tank.x - width / 2, y, width * ratio, height, fill);
    if (isBoss) this.strokeRect(tank.x - width / 2, y, width, height, 0xffc1a5, 1, 0.7);
  }

  private drawBullet(bullet: BulletEntity): void {
    const isPlayerBullet = bullet.ownerTeam === 'player';
    const isArmorPiercing = Boolean(bullet.armorPiercing);
    const isExplosive = Boolean(bullet.explosive);
    const isTracer = Boolean(bullet.tracer);
    const shellColor = isTracer ? 0xf0d49c : isExplosive ? 0x7d8581 : isPlayerBullet ? 0x9ca8a8 : 0xb96856;
    const shellShadow = isTracer ? 0x7d4221 : isPlayerBullet ? 0x4f5a5a : 0x673228;
    const shellHighlight = isTracer ? 0xffffff : isExplosive ? 0xf0d8a0 : isPlayerBullet ? 0xe9f1ee : 0xffcfb7;
    const noseColor = isTracer ? 0xfff0a4 : isExplosive ? 0xff8c24 : isArmorPiercing ? 0xff5a2f : shellHighlight;
    const flameOuter = isPlayerBullet
      ? (isTracer ? 0xff3f1f : isExplosive ? 0xff5a20 : isArmorPiercing ? 0xf22d28 : 0xffb13b)
      : isArmorPiercing ? 0xb265ff : 0xff7040;
    const flameInner = isPlayerBullet
      ? (isTracer ? 0xffffdf : isExplosive ? 0xffe278 : isArmorPiercing ? 0xffe0cc : 0xfff0a8)
      : isArmorPiercing ? 0xf0d8ff : 0xffd9a8;
    const vec = DIR_VECTORS[bullet.direction];
    const side = { x: -vec.y, y: vec.x };
    const length = bullet.size * (isTracer ? 3.05 : isExplosive ? 3.0 : isArmorPiercing ? 5.2 : 3.45);
    const halfWidth = bullet.size * (isTracer ? 0.34 : isExplosive ? 0.58 : isArmorPiercing ? 0.34 : 0.46);
    const noseLength = bullet.size * (isTracer ? 0.86 : isExplosive ? 0.62 : isArmorPiercing ? 0.95 : 0.76);
    const nose = pointAlong(bullet.x, bullet.y, vec, length * 0.5);
    const coneBase = pointAlong(nose.x, nose.y, vec, -noseLength);
    const tail = pointAlong(bullet.x, bullet.y, vec, -length * 0.5);
    const flameTipOuter = pointAlong(tail.x, tail.y, vec, -length * (isTracer ? 0.86 : isExplosive ? 0.54 : isArmorPiercing ? 0.58 : 0.42));
    const flameTipInner = pointAlong(tail.x, tail.y, vec, -length * (isTracer ? 0.6 : isExplosive ? 0.36 : isArmorPiercing ? 0.42 : 0.28));
    const flameWidth = halfWidth * (isTracer ? 1.42 : isExplosive ? 1.18 : isArmorPiercing ? 1.25 : 0.86);
    const finBack = pointAlong(tail.x, tail.y, vec, -bullet.size * 0.76);
    const finInset = pointAlong(tail.x, tail.y, vec, bullet.size * 0.32);
    const finSpread = halfWidth + bullet.size * (isTracer ? 0.28 : isArmorPiercing ? 0.38 : 0.2);
    if (isTracer) {
      const trailBack = pointAlong(tail.x, tail.y, vec, -length * 1.9);
      const trailMid = pointAlong(tail.x, tail.y, vec, -length * 1.05);
      this.drawPoly([
        tail.x + side.x * flameWidth * 0.95, tail.y + side.y * flameWidth * 0.95,
        trailBack.x, trailBack.y,
        tail.x - side.x * flameWidth * 0.95, tail.y - side.y * flameWidth * 0.95,
      ], 0xff4a1f, 0.24);
      this.drawPoly([
        tail.x + side.x * flameWidth * 0.38, tail.y + side.y * flameWidth * 0.38,
        trailMid.x, trailMid.y,
        tail.x - side.x * flameWidth * 0.38, tail.y - side.y * flameWidth * 0.38,
      ], 0xffffd0, 0.42);
      this.strokePath([tail.x, tail.y, trailBack.x, trailBack.y], 0xff8a2a, 1.4, 0.45);
    }
    this.drawPoly([
      coneBase.x + side.x * halfWidth, coneBase.y + side.y * halfWidth,
      tail.x + side.x * halfWidth, tail.y + side.y * halfWidth,
      tail.x - side.x * halfWidth, tail.y - side.y * halfWidth,
      coneBase.x - side.x * halfWidth, coneBase.y - side.y * halfWidth,
    ], shellColor);
    this.strokePath([
      coneBase.x + side.x * halfWidth, coneBase.y + side.y * halfWidth,
      tail.x + side.x * halfWidth, tail.y + side.y * halfWidth,
      tail.x - side.x * halfWidth, tail.y - side.y * halfWidth,
      coneBase.x - side.x * halfWidth, coneBase.y - side.y * halfWidth,
      coneBase.x + side.x * halfWidth, coneBase.y + side.y * halfWidth,
    ], shellShadow, 1.1, 0.78);
    this.drawPoly([
      nose.x, nose.y,
      coneBase.x + side.x * halfWidth, coneBase.y + side.y * halfWidth,
      coneBase.x - side.x * halfWidth, coneBase.y - side.y * halfWidth,
    ], noseColor);
    this.strokePath([
      nose.x, nose.y,
      coneBase.x + side.x * halfWidth, coneBase.y + side.y * halfWidth,
      coneBase.x - side.x * halfWidth, coneBase.y - side.y * halfWidth,
      nose.x, nose.y,
    ], shellShadow, 1.1, 0.82);
    if (isArmorPiercing) {
      for (const sign of [-1, 1]) {
        this.drawPoly([
          finInset.x + side.x * halfWidth * sign, finInset.y + side.y * halfWidth * sign,
          finBack.x + side.x * finSpread * sign, finBack.y + side.y * finSpread * sign,
          tail.x + side.x * halfWidth * 0.18 * sign, tail.y + side.y * halfWidth * 0.18 * sign,
        ], shellShadow, 0.82);
      }
      this.fillCircle(nose.x, nose.y, halfWidth * 1.35, 0xff3228, 0.34);
    }
    if (isTracer) {
      for (const sign of [-1, 1]) {
        this.drawPoly([
          finInset.x + side.x * halfWidth * sign, finInset.y + side.y * halfWidth * sign,
          finBack.x + side.x * finSpread * sign, finBack.y + side.y * finSpread * sign,
          tail.x + side.x * halfWidth * 0.12 * sign, tail.y + side.y * halfWidth * 0.12 * sign,
        ], 0xffb444, 0.72);
      }
      this.fillCircle(nose.x, nose.y, halfWidth * 1.55, 0xfff29a, 0.38);
      this.fillCircle(nose.x, nose.y, halfWidth * 0.82, 0xffffff, 0.42);
    }
    if (isExplosive) {
      const band = pointAlong(tail.x, tail.y, vec, length * 0.32);
      this.strokePath([
        band.x + side.x * halfWidth * 0.95, band.y + side.y * halfWidth * 0.95,
        band.x - side.x * halfWidth * 0.95, band.y - side.y * halfWidth * 0.95,
      ], 0xffb344, 2.2, 0.82);
      this.fillCircle(nose.x, nose.y, halfWidth * 1.15, 0xff7a21, 0.25);
    }
    this.drawPoly([
      tail.x + side.x * flameWidth, tail.y + side.y * flameWidth,
      flameTipOuter.x, flameTipOuter.y,
      tail.x - side.x * flameWidth, tail.y - side.y * flameWidth,
    ], flameOuter, isTracer ? 0.82 : isExplosive ? 0.72 : isArmorPiercing ? 0.72 : 0.5);
    this.drawPoly([
      tail.x + side.x * flameWidth * 0.42, tail.y + side.y * flameWidth * 0.42,
      flameTipInner.x, flameTipInner.y,
      tail.x - side.x * flameWidth * 0.42, tail.y - side.y * flameWidth * 0.42,
    ], flameInner, isTracer ? 0.9 : isExplosive ? 0.8 : isArmorPiercing ? 0.82 : 0.64);
    this.strokePath([
      coneBase.x + side.x * halfWidth * 0.38, coneBase.y + side.y * halfWidth * 0.38,
      tail.x + side.x * halfWidth * 0.38, tail.y + side.y * halfWidth * 0.38,
    ], shellHighlight, 1.2, 0.72);
    this.strokePath([
      tail.x + side.x * halfWidth, tail.y + side.y * halfWidth,
      tail.x - side.x * halfWidth, tail.y - side.y * halfWidth,
    ], shellShadow, 1, 0.72);
  }

  private drawMuzzleFlash(tank: TankEntity): void {
    const vec = DIR_VECTORS[tank.direction];
    const side = { x: -vec.y, y: vec.x };
    const base = pointAlong(tank.x, tank.y, vec, tank.size / 2 + 10);
    const tip = pointAlong(base.x, base.y, vec, 10);
    const w = 5 + tank.shootWindupTimer * 12;
    this.drawPoly([tip.x, tip.y, base.x + side.x * w, base.y + side.y * w, base.x - side.x * w, base.y - side.y * w], 0xffd65a, 0.38);
  }

  private drawExplosionEffects(effects: ExplosionEffectEntity[]): void {
    for (const effect of effects) {
      const alpha = Math.max(0, effect.life / effect.maxLife);
      if (effect.kind === 'shockwave') {
        this.strokeCircle(effect.x, effect.y, Math.max(4, effect.size * 0.5), effect.color, 3, 0.34 * alpha);
        this.strokeCircle(effect.x, effect.y, Math.max(2, effect.size * 0.28), 0xffffff, 1, 0.18 * alpha);
      } else if (effect.kind === 'burst') {
        for (let i = 0; i < 8; i += 1) {
          const angle = (i / 8) * Math.PI * 2;
          this.strokePath([
            effect.x + Math.cos(angle) * effect.size * 0.14,
            effect.y + Math.sin(angle) * effect.size * 0.14,
            effect.x + Math.cos(angle) * effect.size * 0.5,
            effect.y + Math.sin(angle) * effect.size * 0.5,
          ], effect.color, 2, 0.45 * alpha);
        }
      } else {
        this.fillCircle(effect.x, effect.y, effect.size * 0.42, effect.color, 0.2 * alpha);
        this.fillCircle(effect.x, effect.y, effect.size * 0.22, 0xfff2a0, 0.38 * alpha);
      }
    }
  }

  private drawPowerup(powerup: PowerupEntity): void {
    const y = powerup.y + Math.sin(powerup.bob) * 3;
    const texture = this.powerupTextures.get(powerup.kind);
    if (texture) {
      this.drawImage(texture, powerup.x, y, 36, 36);
      this.strokeCircle(powerup.x, y, 18, 0xffffff, 1, 0.28);
      return;
    }
    const color = POWERUP_COLORS[powerup.kind] ?? 0xffffff;
    this.roundRect(powerup.x - 13, y - 13, 26, 26, 6, color);
    this.strokeRoundRect(powerup.x - 13, y - 13, 26, 26, 6, 0xffffff, 1, 0.55);
    this.fillCircle(powerup.x, y, 4, 0x10140f);
  }

  private drawParticle(particle: ParticleEntity): void {
    const alpha = Math.max(0, particle.life / particle.maxLife);
    this.fillCircle(particle.x, particle.y, particle.size, particle.color, alpha);
  }

  private drawBaseShield(engine: GameEngine): void {
    if (!engine.isBaseProtected()) return;
    this.strokeRoundRect(8 * CELL_SIZE - 2, 12 * CELL_SIZE - 2, 4 * CELL_SIZE + 4, 3 * CELL_SIZE + 4, 10, 0x70ecff, 3, 0.7);
  }

  private drawStatusLabel(engine: GameEngine): void {
    if (engine.status !== 'paused' && engine.status !== 'levelComplete') return;
    if (engine.status === 'levelComplete') {
      this.drawLevelClearSettlement(engine);
      return;
    }
    const ctx = this.ctx;
    ctx.save();
    ctx.globalAlpha = 1;
    ctx.fillStyle = toCss(0xf7e5b0);
    ctx.font = '900 46px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(t('status.paused'), VIEW_WIDTH / 2, VIEW_HEIGHT / 2);
    ctx.restore();
  }

  private drawLevelClearSettlement(engine: GameEngine): void {
    const ctx = this.ctx;
    const total = Math.max(1, engine.mode.levelTransitionDelay);
    const remaining = Math.max(0, engine.levelTimer);
    const progress = clamp((total - remaining) / total, 0, 1);
    const intro = easeOutCubic(clamp(progress / 0.22, 0, 1));
    const seconds = Math.max(1, Math.ceil(remaining));
    const nextLevel = engine.level + 1;
    const clearBonus = 500 + engine.level * 25;
    const centerX = VIEW_WIDTH / 2;
    const centerY = VIEW_HEIGHT * 0.38 - (1 - intro) * 16;
    const panelWidth = 500;
    const panelHeight = 180;
    const scale = 0.94 + intro * 0.06;
    const timerX = panelWidth / 2 - 64;

    ctx.save();
    ctx.globalAlpha = 0.13 * intro;
    ctx.fillStyle = toCss(0x0d120d);
    ctx.fillRect(0, 0, VIEW_WIDTH, VIEW_HEIGHT);
    ctx.restore();

    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.scale(scale, scale);
    ctx.globalAlpha = intro;
    this.roundRect(-panelWidth / 2, -panelHeight / 2, panelWidth, panelHeight, 12, 0xf6e9b6, 0.88 * intro);
    this.strokeRoundRect(-panelWidth / 2, -panelHeight / 2, panelWidth, panelHeight, 12, 0x9d7a34, 3, 0.72 * intro);

    this.roundRect(-panelWidth / 2 + 14, panelHeight / 2 - 18, panelWidth - 28, 8, 4, 0x4a573f, 0.2 * intro);
    this.roundRect(-panelWidth / 2 + 14, panelHeight / 2 - 18, (panelWidth - 28) * progress, 8, 4, seconds <= 3 ? 0xe0b13e : 0x486c4c, 0.95 * intro);
    this.fillCircle(timerX, -panelHeight / 2 + 52, 36, seconds <= 3 ? 0xeac34d : 0x496848, 0.95 * intro);
    this.strokeCircle(timerX, -panelHeight / 2 + 52, 36, 0xfff1b0, 2, 0.76 * intro);

    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    drawText(ctx, t('settlement.kicker'), -panelWidth / 2 + 28, -panelHeight / 2 + 24, 14, 0x6f5524, '800');
    drawText(ctx, t('settlement.title', { level: engine.level }), -panelWidth / 2 + 28, -panelHeight / 2 + 62, 34, 0x2e2617, '900');
    drawText(ctx, t('settlement.bonus', { bonus: clearBonus }), -panelWidth / 2 + 28, panelHeight / 2 - 58, 18, 0x4f3f24, '800');
    drawText(ctx, t('settlement.collect'), -panelWidth / 2 + 28, panelHeight / 2 - 34, 14, 0x735f38, '700');

    ctx.textAlign = 'center';
    drawText(ctx, String(seconds), timerX, -panelHeight / 2 + 50, 38, 0xfff6d0, '900');
    drawText(ctx, t('settlement.next', { level: nextLevel, seconds }), timerX, -panelHeight / 2 + 92, 13, 0x5c4a2f, '800');
    ctx.restore();
  }

  private drawEnemyDebugInfo(enemies: TankEntity[]): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (const enemy of enemies) {
      if (!enemy.alive) continue;
      const line1 = `${enemy.kind.toUpperCase()} ${enemy.aiDirection.toUpperCase()}`;
      const line2 = enemy.debugAiLabel ?? '';
      const width = Math.max(ctx.measureText(line1).width, ctx.measureText(line2).width) + 12;
      const top = enemy.y - enemy.size / 2 - 28;
      this.roundRect(enemy.x - width / 2, top - 16, width, 28, 6, 0x14120f, 0.72);
      this.strokeRoundRect(enemy.x - width / 2, top - 16, width, 28, 6, enemy.kind === 'boss' ? 0xff8a5f : 0xffd780, 1, 0.38);
      ctx.fillStyle = '#fff7d6';
      ctx.font = '700 9px system-ui, sans-serif';
      ctx.fillText(line1, enemy.x, top - 6);
      ctx.font = '700 8px system-ui, sans-serif';
      ctx.fillStyle = '#ffd39a';
      ctx.fillText(line2, enemy.x, top + 6);
    }
    ctx.restore();
  }

  private drawImage(image: CanvasImageSource, centerX: number, centerY: number, width: number, height: number, alpha = 1, rotation = 0, mirrorX = false): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(centerX, centerY);
    if (rotation) ctx.rotate(rotation);
    if (mirrorX) ctx.scale(-1, 1);
    ctx.drawImage(image, -width / 2, -height / 2, width, height);
    ctx.restore();
  }

  private fillRect(x: number, y: number, width: number, height: number, color: number, alpha = 1): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = toCss(color);
    ctx.fillRect(x, y, width, height);
    ctx.restore();
  }

  private strokeRect(x: number, y: number, width: number, height: number, color: number, lineWidth = 1, alpha = 1): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = toCss(color);
    ctx.lineWidth = lineWidth;
    ctx.strokeRect(x, y, width, height);
    ctx.restore();
  }

  private roundRect(x: number, y: number, width: number, height: number, radius: number, color: number, alpha = 1): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = toCss(color);
    roundedPath(ctx, x, y, width, height, radius);
    ctx.fill();
    ctx.restore();
  }

  private strokeRoundRect(x: number, y: number, width: number, height: number, radius: number, color: number, lineWidth = 1, alpha = 1): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = toCss(color);
    ctx.lineWidth = lineWidth;
    roundedPath(ctx, x, y, width, height, radius);
    ctx.stroke();
    ctx.restore();
  }

  private fillCircle(x: number, y: number, radius: number, color: number, alpha = 1): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = toCss(color);
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  private strokeCircle(x: number, y: number, radius: number, color: number, lineWidth = 1, alpha = 1): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = toCss(color);
    ctx.lineWidth = lineWidth;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  private drawShieldEffect(x: number, y: number, radius: number, shieldTimer: number): void {
    const ctx = this.ctx;
    const time = performance.now() * 0.0034;
    const pulse = 1 + Math.sin(time * 2.4) * 0.045;
    const outerRadius = radius * 1.1 * pulse;
    const innerRadius = radius * 0.84;
    const ringRadius = radius * 0.96;
    const alphaBoost = Math.min(1, 0.68 + Math.min(shieldTimer, 2.5) * 0.08);

    ctx.save();
    const fill = ctx.createRadialGradient(x, y, innerRadius * 0.15, x, y, outerRadius);
    fill.addColorStop(0, 'rgba(135, 235, 255, 0.08)');
    fill.addColorStop(0.55, 'rgba(96, 220, 255, 0.16)');
    fill.addColorStop(1, 'rgba(44, 154, 255, 0)');
    ctx.fillStyle = fill;
    ctx.beginPath();
    ctx.arc(x, y, outerRadius, 0, Math.PI * 2);
    ctx.fill();

    ctx.lineCap = 'round';
    ctx.lineWidth = 2.4;
    ctx.strokeStyle = `rgba(175, 246, 255, ${0.78 * alphaBoost})`;
    ctx.beginPath();
    ctx.arc(x, y, ringRadius, time, time + Math.PI * 0.92);
    ctx.stroke();

    ctx.lineWidth = 2.8;
    ctx.strokeStyle = `rgba(68, 205, 255, ${0.92 * alphaBoost})`;
    ctx.beginPath();
    ctx.arc(x, y, ringRadius * 1.06, time + Math.PI * 1.05, time + Math.PI * 1.72);
    ctx.stroke();

    ctx.lineWidth = 1.6;
    ctx.strokeStyle = `rgba(225, 255, 255, ${0.6 * alphaBoost})`;
    ctx.beginPath();
    ctx.arc(x, y, innerRadius, -time * 0.8, -time * 0.8 + Math.PI * 0.66);
    ctx.stroke();
    ctx.restore();
  }

  private drawPoly(points: number[], color: number, alpha = 1): void {
    const ctx = this.ctx;
    if (points.length < 4) return;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = toCss(color);
    ctx.beginPath();
    ctx.moveTo(points[0], points[1]);
    for (let i = 2; i < points.length; i += 2) ctx.lineTo(points[i], points[i + 1]);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  private strokePath(points: number[], color: number, lineWidth = 1, alpha = 1): void {
    const ctx = this.ctx;
    if (points.length < 4) return;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = toCss(color);
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(points[0], points[1]);
    for (let i = 2; i < points.length; i += 2) ctx.lineTo(points[i], points[i + 1]);
    ctx.stroke();
    ctx.restore();
  }
}

function tankTextureKind(tank: TankEntity): string {
  if (tank.team === 'player') {
    const playerPrefix = playerTexturePrefix(tank.playerNum);
    if (tank.playerTankClass && tank.playerTankClass !== 'standard') {
      return `${playerPrefix}-${tank.playerTankClass}-upgrade${tank.upgradeLevel}`;
    }
    return playerPrefix === 'p1' ? `upgrade${tank.upgradeLevel}` : `${playerPrefix}upgrade${tank.upgradeLevel}`;
  }
  if (tank.kind === 'boss') return `boss-${tank.bossVariant ?? 'siege'}`;
  if (tank.kind === 'heavy') return 'heavy';
  if (tank.kind === 'sniper') return 'sniper';
  if (tank.kind === 'assault') return 'assault';
  return 'light';
}

function playerTexturePrefix(playerNum?: number): 'p1' | 'p2' | 'p3' | 'p4' {
  if (playerNum === 2) return 'p2';
  if (playerNum === 3) return 'p3';
  if (playerNum === 4) return 'p4';
  return 'p1';
}

function getTankTextureFrame(tank: TankEntity): {
  direction: Direction;
  mirrorX: boolean;
  offsetX: number;
  offsetY: number;
  scale: number;
} {
  const frame = {
    direction: tank.direction,
    mirrorX: false,
    offsetX: 0,
    offsetY: 0,
    scale: 1,
  };
  return frame;
}

function getTankRenderMetrics(tank: TankEntity): {
  frame: ReturnType<typeof getTankTextureFrame>;
  centerX: number;
  centerY: number;
  size: number;
  shieldRadius: number;
} {
  const frame = getTankTextureFrame(tank);
  const upgradeScale = tank.team === 'player' ? Math.max(0, tank.upgradeLevel - 1) * 0.045 : 0;
  const baseScaleSize = tank.kind === 'boss' ? tank.size * 1.72 : tank.size * (1.78 + upgradeScale);
  const size = baseScaleSize * frame.scale;
  const centerX = tank.x + frame.offsetX;
  const centerY = tank.y + frame.offsetY;
  const shieldRadius = size * (tank.kind === 'boss' ? 0.36 : tank.team === 'player' ? 0.35 : 0.34);
  return { frame, centerX, centerY, size, shieldRadius };
}

function pointAlong(x: number, y: number, vec: { x: number; y: number }, distance: number): { x: number; y: number } {
  return {
    x: x + vec.x * distance,
    y: y + vec.y * distance,
  };
}

function easeOutCubic(value: number): number {
  return 1 - (1 - value) ** 3;
}

function drawText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, fontSize: number, color: number, weight: string): void {
  ctx.fillStyle = toCss(color);
  ctx.font = `${weight} ${fontSize}px system-ui, sans-serif`;
  ctx.fillText(text, x, y);
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Failed to load ${src}`));
    image.src = src;
  });
}

function cropToCanvas(image: HTMLImageElement, cols: number, rows: number, col: number, row: number, outW: number, outH: number, removeChecker: boolean, sourceInset = 0): HTMLCanvasElement {
  const sourceW = image.naturalWidth / cols;
  const sourceH = image.naturalHeight / rows;
  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = requiredContext(canvas);
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(
    image,
    col * sourceW + sourceInset,
    row * sourceH + sourceInset,
    sourceW - sourceInset * 2,
    sourceH - sourceInset * 2,
    0,
    0,
    outW,
    outH,
  );
  if (removeChecker) {
    const pixels = ctx.getImageData(0, 0, outW, outH);
    for (let i = 0; i < pixels.data.length; i += 4) {
      const r = pixels.data[i];
      const g = pixels.data[i + 1];
      const b = pixels.data[i + 2];
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      if (max > 205 && max - min < 28) pixels.data[i + 3] = 0;
    }
    ctx.putImageData(pixels, 0, 0);
  }
  return canvas;
}

function recolorPlayerCanvas(source: HTMLCanvasElement, targetHue: number, targetSaturation: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = source.width;
  canvas.height = source.height;
  const ctx = requiredContext(canvas);
  ctx.drawImage(source, 0, 0);
  const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
  for (let i = 0; i < pixels.data.length; i += 4) {
    const alpha = pixels.data[i + 3];
    if (alpha <= 12) continue;
    const r = pixels.data[i];
    const g = pixels.data[i + 1];
    const b = pixels.data[i + 2];
    if (!shouldRecolorPlayerPixel(r, g, b)) continue;
    const [, saturation, lightness] = rgbToHsl(r, g, b);
    const [nextR, nextG, nextB] = hslToRgb(targetHue, Math.max(targetSaturation, saturation * 0.82), lightness);
    pixels.data[i] = nextR;
    pixels.data[i + 1] = nextG;
    pixels.data[i + 2] = nextB;
  }
  ctx.putImageData(pixels, 0, 0);
  return canvas;
}

function shouldRecolorPlayerPixel(r: number, g: number, b: number): boolean {
  const [, saturation, lightness] = rgbToHsl(r, g, b);
  if (saturation < 0.14 || lightness < 0.16 || lightness > 0.9) return false;
  if (r > 150 && r > g * 1.18 && g > b * 1.12) return false;
  if (r > 150 && b > 150 && Math.abs(r - b) < 40 && g < 110) return false;
  return true;
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const lightness = (max + min) / 2;
  if (max === min) return [0, 0, lightness];
  const delta = max - min;
  const saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
  let hue = 0;
  if (max === red) hue = (green - blue) / delta + (green < blue ? 6 : 0);
  else if (max === green) hue = (blue - red) / delta + 2;
  else hue = (red - green) / delta + 4;
  return [hue * 60, saturation, lightness];
}

function hslToRgb(hue: number, saturation: number, lightness: number): [number, number, number] {
  const normalizedHue = (((hue % 360) + 360) % 360) / 360;
  if (saturation <= 0) {
    const value = Math.round(lightness * 255);
    return [value, value, value];
  }
  const hueToRgb = (p: number, q: number, t: number): number => {
    let value = t;
    if (value < 0) value += 1;
    if (value > 1) value -= 1;
    if (value < 1 / 6) return p + (q - p) * 6 * value;
    if (value < 1 / 2) return q;
    if (value < 2 / 3) return p + (q - p) * (2 / 3 - value) * 6;
    return p;
  };
  const q = lightness < 0.5 ? lightness * (1 + saturation) : lightness + saturation - lightness * saturation;
  const p = 2 * lightness - q;
  return [
    Math.round(hueToRgb(p, q, normalizedHue + 1 / 3) * 255),
    Math.round(hueToRgb(p, q, normalizedHue) * 255),
    Math.round(hueToRgb(p, q, normalizedHue - 1 / 3) * 255),
  ];
}

function centerVisibleContent(source: HTMLCanvasElement, maxContentSize: number): HTMLCanvasElement {
  const srcCtx = requiredContext(source);
  const pixels = srcCtx.getImageData(0, 0, source.width, source.height);
  let minX = source.width;
  let minY = source.height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < source.height; y += 1) {
    for (let x = 0; x < source.width; x += 1) {
      const index = (y * source.width + x) * 4;
      if (pixels.data[index + 3] <= 12) continue;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
  }
  if (maxX < minX || maxY < minY) return source;
  const sourceW = maxX - minX + 1;
  const sourceH = maxY - minY + 1;
  const scale = Math.min(maxContentSize / sourceW, maxContentSize / sourceH, 1.35);
  const drawW = sourceW * scale;
  const drawH = sourceH * scale;
  const canvas = document.createElement('canvas');
  canvas.width = source.width;
  canvas.height = source.height;
  const ctx = requiredContext(canvas);
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(source, minX, minY, sourceW, sourceH, (canvas.width - drawW) / 2, (canvas.height - drawH) / 2, drawW, drawH);
  return canvas;
}

function terrainDetailChoices(environment: EnvironmentKind): number[] {
  if (environment === 'forest') return [3, 4, 5, 6, 7, 11];
  if (environment === 'snow') return [7, 8, 9, 10, 11];
  return [0, 1, 2, 3, 7, 11];
}

function seededDetailRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 0x100000000;
  };
}

function toCss(color: number): string {
  return `#${color.toString(16).padStart(6, '0')}`;
}

function roundedPath(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number): void {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function requiredContext(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas 2D context is not available');
  return context;
}
